import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useInView } from 'react-intersection-observer';
import {
  type CallMetadata,
  type CallRecord,
  type HighlightedCitation,
  type KeyMoment,
  SchedulingOutcomeV2Enum,
} from '@frontend/api-call-intel';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { pendo } from '@frontend/tracking';
import { Text, TextLink, useTooltip } from '@frontend/design-system';
import { useCallIntelDefaultsShallowStore } from '../hooks/use-call-intel-defaults';
import { hasRemovedKeyMoments } from '../utils/call-analysis-util';
import { generateTrackingId } from '../utils/tracking';
import { CallIntelChip } from './chip/call-intel-chip';
import { SchedulingChip } from './chip/scheduling-chip';

const KEY_MOMENT_WORD_LIMIT = 10;

type KeyMomentReasonContentProps = {
  keyMoment: KeyMoment;
  onCitationClick?: (citation: HighlightedCitation) => void;
};

type KeyMomentCardProps = {
  children: React.ReactNode;
  footer?: React.ReactNode;
  keyMoments: KeyMoment[];
  useHorizontalLayout?: boolean;
  onCitationClick?: (citation: HighlightedCitation) => void;
};

type KeyMomentsProps = {
  call?: CallRecord;
  callMetadata?: CallMetadata;
  onCitationClick?: (citation: HighlightedCitation) => void;
};

type RecoveredOpportunityFootnoteProps = {
  isManuallyRecovered: boolean;
};

const getWords = (value: string): string[] => value.trim().split(/\s+/).filter(Boolean);

const KeyMomentReasonContent = ({ keyMoment, onCitationClick }: KeyMomentReasonContentProps) => {
  const { t } = useTranslation('call-intel-core');
  const [isExpanded, setIsExpanded] = useState(false);
  const { inView, ref } = useInView({
    triggerOnce: true,
  });

  const words = getWords(keyMoment.description);
  const shouldTruncate = words.length > KEY_MOMENT_WORD_LIMIT;

  const displayedReason =
    shouldTruncate && !isExpanded ? words.slice(0, KEY_MOMENT_WORD_LIMIT).join(' ') : keyMoment.description;
  const primaryCitation = keyMoment.citations[0];
  const showCitation = !!primaryCitation && (!shouldTruncate || isExpanded);

  useEffect(() => {
    if (!inView) return;

    pendo.track('call-intel-key-moment-reason-seen', {
      keyMomentType: keyMoment.type,
      timestamp: keyMoment.citations[0]?.time,
    });
  }, [inView, keyMoment.citations, keyMoment.type]);

  return (
    <div ref={ref}>
      <Text as='span' css={styles.reasonContent}>
        {displayedReason}
        {shouldTruncate && !isExpanded ? '...' : ''}
      </Text>

      {showCitation && (
        <>
          {' '}
          <TextLink
            css={styles.reasonContent}
            onClick={() => onCitationClick?.(primaryCitation)}
            trackingId={generateTrackingId({
              component: 'key-moment-reason',
              context: `${keyMoment.type}-timestamp-click`,
            })}
          >
            {`(${primaryCitation.time})`}
          </TextLink>
        </>
      )}

      {shouldTruncate && (
        <>
          {' '}
          <TextLink
            css={styles.reasonContent}
            onClick={() => setIsExpanded((current) => !current)}
            trackingId={generateTrackingId({
              component: 'key-moment-reason',
              context: `${keyMoment.type}-${isExpanded ? 'show-less-click' : 'show-more-click'}`,
            })}
          >
            {isExpanded ? t('Show Less') : t('Show More')}
          </TextLink>
        </>
      )}
    </div>
  );
};

const RecoveredOpportunityFootnote = ({ isManuallyRecovered }: RecoveredOpportunityFootnoteProps) => {
  const { t } = useTranslation('call-intel-core');

  const { Tooltip, triggerProps, tooltipProps } = useTooltip({
    placement: 'top',
  });

  const tooltipLabel = isManuallyRecovered
    ? t('Updated manually to Recovered Opportunity — See Call Breakdown for details')
    : t('Updated by Call Intelligence to Recovered Opportunity — See Call Breakdown for details');

  return (
    <>
      <div {...triggerProps} css={styles.recoveredFootnote}>
        <Text as='span' size='small' css={styles.updatedLabel}>
          {t('Updated')}
        </Text>
        <Text as='span' size='small' css={styles.footNoteText}>
          —
        </Text>
        <Icon name='calendar' size={12} css={styles.footNoteIcon} />
        <Text as='span' size='small' css={styles.footNoteText}>
          {t('Recovered')}
        </Text>
      </div>
      <Tooltip {...tooltipProps}>
        <Text size='medium' color='white'>
          {tooltipLabel}
        </Text>
      </Tooltip>
    </>
  );
};

const KeyMomentCard = ({
  children,
  footer,
  keyMoments,
  useHorizontalLayout = false,
  onCitationClick,
}: KeyMomentCardProps) => {
  return (
    <div css={[styles.keyMomentCard, useHorizontalLayout && styles.horizontalKeyMomentCard]}>
      <div css={[styles.keyMomentCardHeader, useHorizontalLayout && styles.horizontalKeyMomentCardHeader]}>
        {children}
      </div>

      <div css={[styles.reasons, useHorizontalLayout && styles.horizontalKeyMomentReasons]}>
        {keyMoments.map((keyMoment) => (
          <KeyMomentReasonContent
            key={`${keyMoment.type}-${keyMoment.description}`}
            keyMoment={keyMoment}
            onCitationClick={onCitationClick}
          />
        ))}
        {footer}
      </div>
    </div>
  );
};

export const KeyMoments = ({ call, callMetadata, onCitationClick }: KeyMomentsProps) => {
  const { t } = useTranslation('call-intel-core');
  const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

  const keyMoments = callMetadata?.keyMoments ?? [];

  const { unscheduledKeyMoments, unhappyKeyMoments } = keyMoments.reduce(
    (acc, keyMoment) => {
      if (keyMoment.type === 'unscheduled') {
        acc.unscheduledKeyMoments.push(keyMoment);
      } else if (keyMoment.type === 'unhappy') {
        acc.unhappyKeyMoments.push(keyMoment);
      }

      return acc;
    },
    {
      unscheduledKeyMoments: [] as KeyMoment[],
      unhappyKeyMoments: [] as KeyMoment[],
    }
  );

  const showUnhappyKeyMoment = unhappyKeyMoments.length > 0;
  const showUnscheduledKeyMoment = unscheduledKeyMoments.length > 0;
  const hasKeyMoments = showUnscheduledKeyMoment || showUnhappyKeyMoment;
  const hasEditedKeyMoments = hasRemovedKeyMoments(call);
  const shouldShowEditedLabel = hasEditedKeyMoments && hasKeyMoments;
  const shouldShowRemovedKeyMomentsMessage = hasEditedKeyMoments && !hasKeyMoments;

  if (!hasKeyMoments && !shouldShowRemovedKeyMomentsMessage) {
    return null;
  }

  const keyMomentCount = Number(showUnscheduledKeyMoment) + Number(showUnhappyKeyMoment);
  const useHorizontalCardLayout = keyMomentCount === 1;
  const sentiment = call?.callSentiment?.sentiment;
  const isRecoveredOpportunity =
    call?.schedulingOutcomeV2 === SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL;
  const isManuallyRecovered = isRecoveredOpportunity && !!call?.isFieldEdited?.isSchedulingOutcomeEdited;

  return (
    <section css={styles.wrapper} data-testid='key-moments'>
      <div css={styles.headerRow}>
        <Text weight='bold' size='medium'>
          {t('Key Moments')}
        </Text>

        {shouldShowEditedLabel && (
          <Text as='span' size='small' color='subdued' css={styles.editedLabel}>
            {t('Edited')}
          </Text>
        )}
      </div>

      {shouldShowRemovedKeyMomentsMessage ? (
        <Text size='small' color='subdued' css={styles.removedKeyMomentsText}>
          {t('Key Moments were removed due to edits to the Conversation Analysis')}
        </Text>
      ) : (
        <div css={styles.keyMomentGrid}>
          {showUnscheduledKeyMoment && (
            <KeyMomentCard
              keyMoments={unscheduledKeyMoments}
              useHorizontalLayout={useHorizontalCardLayout}
              onCitationClick={onCitationClick}
              footer={
                isRecoveredOpportunity ? (
                  <RecoveredOpportunityFootnote isManuallyRecovered={isManuallyRecovered} />
                ) : undefined
              }
            >
              <SchedulingChip
                call={call}
                schedulingOutcome={SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED}
                showTooltip={false}
                customStyles={styles.reasonChip}
              />
            </KeyMomentCard>
          )}

          {showUnhappyKeyMoment && sentiment && (
            <KeyMomentCard
              keyMoments={unhappyKeyMoments}
              useHorizontalLayout={useHorizontalCardLayout}
              onCitationClick={onCitationClick}
            >
              <CallIntelChip
                as='chip'
                chipVariants={chipVariants}
                dataKey={sentiment}
                label={dataLabels.sentiments?.[sentiment] || sentiment}
                hideTooltip
                customStyles={styles.reasonChip}
              />
            </KeyMomentCard>
          )}
        </div>
      )}
    </section>
  );
};

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xs);
    width: 100%;
  `,
  headerRow: css`
    display: flex;
    align-items: center;
    gap: var(--fabric-space-gap-xxs);
  `,
  editedLabel: css`
    font-style: italic;
  `,
  removedKeyMomentsText: css`
    font-style: italic;
    width: 100%;
  `,
  keyMomentGrid: css`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(180px, 100%), 1fr));
    gap: var(--fabric-space-gap-xs);
    align-items: stretch;

    @media screen and (max-width: ${breakpoints.xsmall.max}px) {
      grid-template-columns: 1fr;
    }
  `,
  keyMomentCard: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xs);
    height: 100%;
    padding: var(--fabric-space-gap-xs);
    border: 1px solid var(--fabric-color-border-disabled);
    border-radius: var(--fabric-radius-md);
  `,
  horizontalKeyMomentCard: css`
    flex-direction: row;
    align-items: center;
    gap: var(--fabric-space-gap-xs);
    @media screen and (max-width: ${breakpoints.xsmall.max}px) {
      flex-direction: column;
      align-items: stretch;
    }
  `,
  keyMomentCardHeader: css`
    display: flex;
    align-items: center;
  `,
  horizontalKeyMomentCardHeader: css`
    flex-shrink: 0;
    @media screen and (max-width: ${breakpoints.xsmall.max}px) {
      flex-shrink: 1;
    }
  `,
  reasons: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xs);
    flex: 1;
  `,
  horizontalKeyMomentReasons: css`
    min-width: 0;
  `,
  reasonContent: css`
    font-size: var(--fabric-font-size-xs);
  `,
  reasonChip: css`
    height: var(--fabric-space-gap-md);

    .chip {
      height: var(--fabric-space-gap-md);
      gap: var(--fabric-space-gap-none);
      font-size: var(--fabric-font-size-xs);
      padding: var(--fabric-space-padding-none) var(--fabric-space-padding-xxs);
      > svg {
        height: var(--fabric-space-gap-sm);
        width: var(--fabric-space-gap-sm);
      }
    }
  `,
  recoveredFootnote: css`
    display: flex;
    align-items: center;
    gap: var(--fabric-space-gap-xxs);
    width: fit-content;
    cursor: pointer;
  `,
  updatedLabel: css`
    color: var(--fabric-color-text-muted);
    font-style: italic;
  `,
  footNoteText: css`
    color: var(--fabric-color-text-muted);
  `,
  footNoteIcon: css`
    color: var(--fabric-color-accent-seaweed-base);
  `,
};
